/*
 * Copyright (c) Hilo 2020.
 * Author: fharold
 *
 * hilo-oauth-manager
 * HomePage.tsx
 */

import {PolicyListener} from "../../services/PolicyService";
import Policy from "../../tools/provider/Policy";
import HiloReactComponent from "../../tools/view/layout/HiloReactComponent";
import React from "react";
import PageTitle, {TitleTab} from "../../tools/view/layout/PageTitle";
import UserEditIdentityForm from "../../models/form/user/UserEditIdentityForm";
import User from "../../models/User";
import UserService, {UserListener} from "../../services/UserService";
import Spinner from "../../tools/view/element/Spinner";
import FormSection from "../../tools/view/form/FormSection";
import UserEditPasswordForm from "../../models/form/user/UserEditPasswordForm";
import UIToast from "../../tools/view/toast/UIToast";
import {AbstractForm, FormListener} from "../../tools/model/form/AbstractForm";
import {UserEditIdentityDTO} from "../../models/dto/user/UserEditIdentityDTO";
import {UserEditPasswordDTO} from "../../models/dto/user/UserEditPasswordDTO";
import PageComponent from "../../tools/view/layout/PageComponent";

interface State {
    policy: Policy;
    pageIndex: number;
    userEditIdentityForm?: UserEditIdentityForm;
    userEditPasswordForm?: UserEditPasswordForm;
    currentUser?: User;
}

export default class SettingsPage
    extends HiloReactComponent<{}, State>
    implements PolicyListener, UserListener, FormListener<UserEditIdentityForm | UserEditPasswordForm, UserEditIdentityDTO | UserEditPasswordDTO> {
    private readonly _userService : UserService;

    //region React Lifecycle
    /**
     * constructor
     * @param props
     * @param state
     */
    constructor(props: {}, state: State) {
        super(props, state);

        this._userService = this._serviceRepository.resolve(UserService.id) as UserService;

        const user = this._userService.user;

        this.state = {
            policy: this._policyService.policy,
            pageIndex: 0,
            currentUser: user
        };
    }

    /**
     * componentDidMount
     */
    public componentDidMount(): void {
        super.componentDidMount();
        this._policyService.addListener(this);

        this.refreshForms();
        this._userService.addListener(this);
    }

    /**
     * componentWillUnmount
     */
    public componentWillUnmount(): void {
        this._userService.removeListener(this);
        this._policyService.removeListener(this);
        super.componentWillUnmount();
    }

    //endregion

    //region Listeners
    /**
     * refreshForms
     */
    private refreshForms = async () => {
        const {currentUser, userEditIdentityForm, userEditPasswordForm} = this.state;

        if (!currentUser) return;

        if (!userEditPasswordForm) {
            await this.setState({userEditPasswordForm: new UserEditPasswordForm()})
            this.state.userEditPasswordForm?.addListener(this);
        }

        if (!userEditIdentityForm) {
            await this.setState({userEditIdentityForm: new UserEditIdentityForm(currentUser)})
            this.state.userEditIdentityForm?.addListener(this);
        }

        this.state.userEditIdentityForm?.load(UserEditIdentityDTO.fromUser(currentUser));
    };

    /**
     * policyUpdated
     * @param policy
     */
    public policyUpdated = (policy: Policy) => this.setState({policy: policy})

    /**
     * onFormUpdate
     * @param form
     */
    public onFormUpdate = (form: AbstractForm<UserEditIdentityDTO | UserEditPasswordDTO>) => {
        if (form instanceof UserEditPasswordForm) {
            this.setState({userEditPasswordForm: form});
        } else {
            this.setState({userEditIdentityForm: form})
        }
    };

    /**
     * currentUserHasBeenUpdated
     * @param user
     * @returns {Promise<void>}
     */
    public async currentUserHasBeenUpdated(user: User | undefined) {
        await this.setState({currentUser: user});
        this.refreshForms();
    }

    //endregion

    //region Rendering
    /**
     * _editProfile
     * @param props
     * @private
     */
    private _editProfile = (props: {
        userEditIdentityForm?: UserEditIdentityForm,
        userEditPasswordForm?: UserEditPasswordForm
    }) => {
        if (!props.userEditIdentityForm || !props.userEditPasswordForm) {
            return <Spinner large={true}/>
        }

        return <>
            <FormSection label={this.t.identity} form={props.userEditIdentityForm} submit={this._saveIdentityForm}/>
            <FormSection label={this.t.loginFormPwdPh} form={props.userEditPasswordForm}
                         submit={this._savePasswordForm}/>
        </>
    }

    /**
     * _saveIdentityForm
     * @private
     */
    private _saveIdentityForm = async (dto: FormData) => {
        const {currentUser, userEditIdentityForm} = this.state;
        if (!currentUser || !userEditIdentityForm) return;

        try {
            await this._userService.updateUserIdentity(currentUser, dto);

            userEditIdentityForm.statusCode = 201;
            UIToast.default.show(this.t.saved);

            await this._userService.refreshCurrentUser();
        } catch (e) {
            userEditIdentityForm.statusCode = e.status;
        }
    };

    /**
     * _savePasswordForm
     * @private
     */
    private _savePasswordForm = async (dto: FormData) => {
        const {currentUser, userEditPasswordForm} = this.state;
        if (!currentUser || !userEditPasswordForm) return;

        try {
            await this._userService.updateUserPassword(currentUser, dto);

            userEditPasswordForm.statusCode = 201;
            UIToast.default.show(this.t.saved);

            await this._userService.refreshCurrentUser();
        } catch (e) {
            userEditPasswordForm.statusCode = e.status;
        }
    };

    /**
     * render
     */
    public render(): JSX.Element {
        const {userEditIdentityForm, userEditPasswordForm} = this.state;

        const titleTabs: TitleTab[] = [
            {
                title: t => t.settings,
                content: <this._editProfile userEditIdentityForm={userEditIdentityForm}
                                            userEditPasswordForm={userEditPasswordForm}
                />
            },
        ];

        return <PageComponent>
            <PageTitle title={t => t.settings} tabs={titleTabs}
                       onTabSelected={index => this.setState({pageIndex: index})}/>
            {titleTabs[this.state.pageIndex].content}
        </PageComponent>
    }
    //endregion
}